import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { ExternalLink } from "lucide-react";

import { cn } from "../../lib/utils";

const linkVariants = cva(
  "inline-flex items-center gap-1 whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none  decoration-1 underline-offset-4",
  {
    variants: {
      type: {
        anchor: "hover:underline",
        action: "no-underline cursor-pointer",
      },
      color: {
        primary: "",
        positive: "",
        negative: "",
        notice: "",
        info: "",
        neutral: "",
        white: "",
      },
      size: {
        xsmall: "text-body-xsmall-medium gap-1",
        small: "text-body-small-medium gap-1",
        medium: "text-body-medium-medium gap-1.5",
        large: "text-body-large-medium gap-1.5",
      },
      isIconOnly: {
        true: "no-underline",
        false: "",
      },
      isDisabled: {
        true: "cursor-not-allowed opacity-50",
        false: "cursor-pointer",
      },
    },
    compoundVariants: [
      // Primary color variants
      {
        color: "primary",
        class: `text-action-ink-primary-normal 
          hover:text-action-ink-primary-subtle
          hover:decoration-action-outline-primary-hover 
          disabled:text-action-ink-primary-disabled
          focus:text-action-ink-primary-hover
          `,
      },
      // Positive color variants
      {
        color: "positive",
        class: `text-action-ink-positive-normal 
          hover:text-action-ink-positive-subtle
          hover:decoration-action-outline-positive-hover 
          hover:text-action-ink-positive-hover 
          disabled:text-action-ink-positive-disabled
          focus:text-action-ink-positive-hover
          `,
      },
      // Negative color variants
      {
        color: "negative",
        class: `text-action-ink-negative-normal 
          hover:text-action-ink-negative-subtle
          hover:decoration-action-outline-negative-hover 
          hover:text-action-ink-negative-hover 
          disabled:text-action-ink-negative-disabled
          focus:text-action-ink-negative-hover
          `,
      },
      // Notice color variants
      {
        color: "notice",
        class: `text-action-ink-notice-normal 
          hover:text-action-ink-notice-subtle
          hover:decoration-action-outline-notice-hover 
          hover:text-action-ink-notice-hover 
          disabled:text-action-ink-notice-disabled
          focus:text-action-ink-notice-hover
          `,
      },
      // Info color variants
      {
        color: "info",
        class: `text-action-ink-info-normal 
          hover:text-action-ink-info-subtle
          hover:decoration-action-outline-info-hover 
          hover:text-action-ink-info-hover 
          disabled:text-action-ink-info-disabled
          focus:text-action-ink-info-hover
          `,
      },
      // Neutral color variants
      {
        color: "neutral",
        class: `text-action-ink-neutral-normal 
          hover:text-action-ink-neutral-subtle
          hover:decoration-action-outline-neutral-hover 
          hover:text-action-ink-neutral-hover 
          disabled:text-action-ink-neutral-disabled
          focus:text-action-ink-neutral-hover
          `,
      },
      {
        color: "white",
        class: `text-surface-ink-white-normal 
          hover:text-surface-ink-white-subtle
          hover:text-action-ink-white-hover 
          disabled:text-action-ink-white-muted
          focus:text-action-ink-white-hover
          `,
      },
    ],
    defaultVariants: {
      type: "anchor",
      color: "primary",
      size: "medium",
      isIconOnly: false,
      isDisabled: false,
    },
  }
);

export interface LinkProps
  extends Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "color"> {
  type?: "anchor" | "action";
  color?:
    | "primary"
    | "positive"
    | "negative"
    | "notice"
    | "info"
    | "neutral"
    | "white";
  size?: "xsmall" | "small" | "medium" | "large";
  isIconOnly?: boolean;
  isDisabled?: boolean;
  asChild?: boolean;
  showIcon?: boolean;
  icon?: React.ReactNode;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      className,
      type = "anchor",
      color = "primary",
      size = "medium",
      isIconOnly = false,
      isDisabled = false,
      asChild = false,
      showIcon = false,
      icon,
      leadingIcon,
      trailingIcon,
      children,
      onClick,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "a";

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (isDisabled) {
        e.preventDefault();
        return;
      }
      onClick?.(e);
    };

    // Icon size based on link size
    const iconSize = {
      xsmall: 12,
      small: 14,
      medium: 16,
      large: 18,
    }[size];

    // Determine what to show as trailing icon
    // Priority: trailingIcon > (showIcon && icon) > (showIcon && default ExternalLink)
    const finalTrailingIcon =
      trailingIcon || (showIcon && (icon || <ExternalLink size={iconSize} />));

    const linkContent = (
      <>
        {leadingIcon && !isIconOnly && (
          <span className="inline-flex items-center">{leadingIcon}</span>
        )}
        {!isIconOnly && children}
        {isIconOnly && children}
        {finalTrailingIcon && !isIconOnly && (
          <span className="inline-flex items-center">{finalTrailingIcon}</span>
        )}
        {isIconOnly &&
          (leadingIcon || finalTrailingIcon || (
            <ExternalLink size={iconSize} />
          ))}
      </>
    );

    return (
      <Comp
        className={cn(
          linkVariants({
            type,
            color,
            size,
            isIconOnly,
            isDisabled,
          }),
          className
        )}
        ref={ref}
        onClick={handleClick}
        aria-disabled={isDisabled}
        tabIndex={isDisabled ? -1 : undefined}
        {...props}
      >
        {linkContent}
      </Comp>
    );
  }
);
Link.displayName = "Link";

export { Link, linkVariants };
